import React from 'react';
import {
  SUNDAYS,
  type GivingBreakdown,
  type WorshipWeeklyState,
  avgWorship,
} from '../../utils/mbcbReportShared';
import type { EditorStep } from './editorSteps';

export interface ReportSummaryData {
  ministryHighlights: string;
  prayerRequests: string;
  supportNeeded: string;
  additionalNotes: string;
  nextMonthFocus: string;
  attendanceNotes: string;
  givingNotes: string;
  weekly: WorshipWeeklyState;
  giving: GivingBreakdown;
  newMembers: number;
  baptisms: number;
  salvations: number;
  budgetReceiptsTotal: number;
  missionGivingTotal: number;
}

interface ReportSummaryProps {
  data: ReportSummaryData;
  ministryLabel: string;
  onEdit?: (step: EditorStep) => void;
  compact?: boolean;
}

const formatUsd = (n: number) =>
  n.toLocaleString('en-US', { style: 'currency', currency: 'USD', minimumFractionDigits: 2 });

const SummarySection: React.FC<{
  title: string;
  editStep?: EditorStep;
  onEdit?: (step: EditorStep) => void;
  children: React.ReactNode;
  accent?: 'amber' | 'emerald' | 'neutral';
}> = ({ title, editStep, onEdit, children, accent = 'neutral' }) => {
  const accentClasses =
    accent === 'amber'
      ? 'border-amber-100 bg-gradient-to-br from-amber-50/80 to-white'
      : accent === 'emerald'
        ? 'border-emerald-100 bg-gradient-to-br from-emerald-50/60 to-white'
        : 'border-gray-100 bg-white';

  return (
    <section className={`rounded-2xl border p-4 space-y-3 ${accentClasses}`}>
      <div className="flex items-center justify-between gap-2">
        <h3 className="text-sm font-semibold text-gray-900">{title}</h3>
        {onEdit && editStep && (
          <button
            type="button"
            onClick={() => onEdit(editStep)}
            className="text-xs font-medium text-primary-600 hover:text-primary-700 shrink-0"
          >
            Edit
          </button>
        )}
      </div>
      {children}
    </section>
  );
};

const NarrativeBlock: React.FC<{ label: string; text?: string }> = ({ label, text }) => {
  if (!text?.trim()) return null;
  return (
    <div>
      <p className="text-xs font-medium text-gray-500">{label}</p>
      <p className="text-sm text-gray-800 mt-0.5 whitespace-pre-wrap">{text.trim()}</p>
    </div>
  );
};

export const ReportSummary: React.FC<ReportSummaryProps> = ({
  data,
  ministryLabel,
  onEdit,
  compact = false,
}) => {
  const worshipAverage = Math.round(avgWorship(data.weekly) * 10) / 10;

  return (
    <div className={`space-y-4 ${compact ? '' : 'pb-4'}`}>
      <SummarySection title="People & story" editStep="people" onEdit={onEdit} accent="amber">
        <NarrativeBlock label={`What God did in ${ministryLabel}`} text={data.ministryHighlights} />
        <NarrativeBlock label="Prayer requests" text={data.prayerRequests} />
        <NarrativeBlock label="How the network can help" text={data.supportNeeded} />
        {!data.ministryHighlights?.trim() &&
          !data.prayerRequests?.trim() &&
          !data.supportNeeded?.trim() && (
            <p className="text-sm text-gray-500 italic">No story shared yet.</p>
          )}
      </SummarySection>

      <SummarySection title="Attendance & counts" editStep="numbers" onEdit={onEdit}>
        <div className="grid grid-cols-3 gap-3 text-center">
          <div className="rounded-xl bg-gray-50 px-2 py-2">
            <p className="text-xs text-gray-500">Weekly average</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums">{worshipAverage}</p>
          </div>
          <div className="rounded-xl bg-gray-50 px-2 py-2">
            <p className="text-xs text-gray-500">Baptisms</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums">{data.baptisms}</p>
          </div>
          <div className="rounded-xl bg-gray-50 px-2 py-2">
            <p className="text-xs text-gray-500">New members</p>
            <p className="text-lg font-semibold text-gray-900 tabular-nums">{data.newMembers}</p>
          </div>
        </div>
        {!compact && (
          <>
            <div className="grid grid-cols-5 gap-2">
              {SUNDAYS.map((sk, idx) => (
                <div key={sk} className="text-center">
                  <p className="text-[10px] text-gray-500">Sun {idx + 1}</p>
                  <p className="text-sm font-medium tabular-nums">{data.weekly.worshipAttendance[sk]}</p>
                </div>
              ))}
            </div>
            {data.salvations > 0 && (
              <p className="text-sm text-gray-600">
                Faith decisions: <strong>{data.salvations}</strong>
              </p>
            )}
            <NarrativeBlock label="Attendance notes" text={data.attendanceNotes} />
          </>
        )}
      </SummarySection>

      <SummarySection title="Generosity & giving" editStep="generosity" onEdit={onEdit} accent="emerald">
        <NarrativeBlock label="Generosity stories" text={data.additionalNotes} />
        <div className="grid grid-cols-2 gap-3">
          <div className="rounded-xl bg-white/80 border border-emerald-100 px-3 py-2">
            <p className="text-xs text-gray-500">Budget receipts</p>
            <p className="text-base font-semibold text-gray-900 tabular-nums">
              {formatUsd(data.budgetReceiptsTotal)}
            </p>
          </div>
          <div className="rounded-xl bg-white/80 border border-emerald-100 px-3 py-2">
            <p className="text-xs text-gray-500">Mission giving</p>
            <p className="text-base font-semibold text-gray-900 tabular-nums">
              {formatUsd(data.missionGivingTotal)}
            </p>
          </div>
        </div>
        <NarrativeBlock label="Giving notes" text={data.givingNotes} />
        <NarrativeBlock label="Hope for next month" text={data.nextMonthFocus} />
      </SummarySection>
    </div>
  );
};

/** Compact stats row for thank-you screen */
export const ReportRecapStats: React.FC<{
  worshipAverage: number;
  baptisms: number;
  budgetTotal: number;
  missionTotal: number;
}> = ({ worshipAverage, baptisms, budgetTotal, missionTotal }) => (
  <div className="grid grid-cols-2 gap-3 w-full max-w-sm">
    <div className="rounded-xl bg-white border border-primary-100 px-3 py-3 text-center shadow-sm">
      <p className="text-xs text-gray-500">Avg attendance</p>
      <p className="text-xl font-semibold text-primary-800 tabular-nums">{worshipAverage}</p>
    </div>
    <div className="rounded-xl bg-white border border-primary-100 px-3 py-3 text-center shadow-sm">
      <p className="text-xs text-gray-500">Baptisms</p>
      <p className="text-xl font-semibold text-primary-800 tabular-nums">{baptisms}</p>
    </div>
    <div className="rounded-xl bg-white border border-primary-100 px-3 py-3 text-center shadow-sm">
      <p className="text-xs text-gray-500">Budget</p>
      <p className="text-lg font-semibold text-primary-800 tabular-nums">
        {formatUsd(budgetTotal)}
      </p>
    </div>
    <div className="rounded-xl bg-white border border-primary-100 px-3 py-3 text-center shadow-sm">
      <p className="text-xs text-gray-500">Mission</p>
      <p className="text-lg font-semibold text-primary-800 tabular-nums">
        {formatUsd(missionTotal)}
      </p>
    </div>
  </div>
);
